/* WP-10 Collect list (§14.5.3): active collections as situation-style
 * entries, past as timeline entries, empty state. */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalApi } from '../api/portal-api';
import type { PickupRequestListItem, PickupRequestsResponse } from '../api/types';
import { EmptyState, SectionHeader, TextLink } from '../components/primitives';
import { TimelineEntry } from '../components/timeline';
import { CheckIcon, CollectIcon, CloseIcon } from '../icons';
import { EmptyStateIllustration } from '../illustrations';
import { currentPortalLocale } from '../i18n';
import { formatDate } from '../utils/dates';
import { errorText } from '../utils/error-text';

// Terminal customer statuses — everything else is still on the way.
const PAST_STATUSES = new Set(['COLLECTED', 'REJECTED', 'CANCELLED', 'EXPIRED']);
const DONE_OK = new Set(['COLLECTED']);

export function CollectPage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const navigate = useNavigate();
  const [items, setItems] = useState<PickupRequestListItem[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    portalApi
      .get<PickupRequestsResponse>('/pickup-requests')
      .then((res) => {
        setItems(res.items);
        setNextCursor(res.nextCursor ?? null);
      })
      .catch((err) => setError(errorText(err, t)));
  }, [t]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await portalApi.get<PickupRequestsResponse>(
        `/pickup-requests?cursor=${encodeURIComponent(nextCursor)}`,
      );
      setItems((prev) => [...(prev ?? []), ...res.items]);
      setNextCursor(res.nextCursor ?? null);
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setLoadingMore(false);
    }
  };

  const active = (items ?? []).filter((i) => !PAST_STATUSES.has(i.customerStatus));
  const past = (items ?? []).filter((i) => PAST_STATUSES.has(i.customerStatus));

  return (
    <>
      <h1 className="hp-display-xl">{t('collect.title')}</h1>

      <TextLink
        onClick={() => navigate('/collect/new')}
        data-quality-id="portal.collect.new"
      >
        {t('collect.newCta')}
      </TextLink>

      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}

      {items && items.length === 0 ? (
        <EmptyState
          illustration={<EmptyStateIllustration title={t('collect.empty.title')} />}
          sentence={t('collect.empty.title')}
          action={
            <button type="button" className="hp-textlink" onClick={() => navigate('/humidor')}>
              {t('collect.empty.cta')}
            </button>
          }
        />
      ) : null}

      {active.length ? (
        <section className="hp-section">
          <SectionHeader title={t('collect.active')} count={active.length} />
          <div className="hp-rows">
            {active.map((r) => (
              <button
                key={r.id}
                type="button"
                className="hp-listrow"
                onClick={() => navigate(`/collect/${r.id}`)}
                data-quality-id={`portal.collect.open.${r.requestNumber}`}
              >
                <CollectIcon size={20} />
                <span>
                  <strong>
                    {t(`collect.detail.status.${r.customerStatus}`, {
                      shop: r.pickupShop?.name ?? '',
                    })}
                  </strong>
                  <span className="hp-listrow__sub">
                    {r.pickupShop?.name} · {formatDate(r.preferredPickupDate, locale)} ·{' '}
                    {t('common.sticks', { count: r.totalSticks })}
                  </span>
                </span>
                <span className="hp-listrow__chev">›</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {past.length ? (
        <section className="hp-section">
          <SectionHeader title={t('collect.past')} count={past.length} />
          <div className="hp-timeline">
            {past.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => navigate(`/collect/${r.id}`)}
                style={{ all: 'unset', cursor: 'pointer', display: 'block' }}
                data-quality-id={`portal.collect.open.${r.requestNumber}`}
              >
                <TimelineEntry
                  date={r.submittedAt}
                  dateLabel={formatDate(r.submittedAt, locale, { month: 'short' })}
                  icon={
                    r.customerStatus === 'COLLECTED' ? (
                      <CheckIcon size={14} />
                    ) : (
                      <CloseIcon size={14} />
                    )
                  }
                  ok={DONE_OK.has(r.customerStatus)}
                >
                  {t(`collect.detail.status.${r.customerStatus}`, {
                    shop: r.pickupShop?.name ?? '',
                  })}
                  <span className="hp-listrow__sub">{r.requestNumber}</span>
                </TimelineEntry>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {nextCursor ? (
        <TextLink onClick={loadMore} chevron={false} data-quality-id="portal.collect.more">
          {loadingMore ? t('common.loading') : t('common.loadMore')}
        </TextLink>
      ) : null}
    </>
  );
}
